import { SortDirection } from "api/models";
import { useState, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import { useQueryWrapper, useSelectedShop } from "shared/hooks";
import { getSubscriptionsData } from "../api";
import { useDateRangeContext } from "./useDateRangeContext";
import {
  DataTablePaginationData,
  DataTableSortingData,
  SubscriptionWithStatus,
  SubscriptionStatus
} from "../types";

export const useSubscriptionsData = () => {
  const { selectedShopId } = useSelectedShop();

  const [searchValue, setSearchValue] = useState<string>("");

  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>(searchValue);

  const debouncedSearchUpdate = useCallback((value: string) => {
    setDebouncedSearchValue(value.length >= 3 ? value : "");
  }, []);

  const debouncedSearch = useMemo(() => {
    return debounce(debouncedSearchUpdate, 500);
  }, [debouncedSearchUpdate]);

  const { startDate, endDate } = useDateRangeContext();

  const [sort, setSort] = useState<DataTableSortingData | undefined>({
    field: "timeOfSubscription",
    direction: SortDirection.DESC
  });

  const [pagination, setPagination] = useState<DataTablePaginationData>({
    page: 0,
    pageSize: 10
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleSortingChange = useCallback((sort: DataTableSortingData | undefined) => {
    setSort(sort);
  }, []);

  const handlePaginationChange = useCallback((pagination: DataTablePaginationData) => {
    setPagination(pagination);
  }, []);

  const queryKey = useMemo(
    () => [
      "subscriptionsData",
      selectedShopId,
      startDate?.toString(),
      endDate?.toString(),
      debouncedSearchValue,
      sort?.field,
      sort?.direction?.toString(),
      pagination.page,
      pagination.pageSize
    ],
    [selectedShopId, startDate, endDate, debouncedSearchValue, sort, pagination]
  );

  const { data, isLoading, isFetching } = useQueryWrapper(
    queryKey,
    async () =>
      await getSubscriptionsData({
        shopId: selectedShopId!,
        page: pagination.page,
        pageSize: pagination.pageSize,
        startDate: startDate!,
        endDate: endDate!,
        sortDirection: (sort?.direction as SortDirection) ?? undefined,
        sortKey: sort?.field,
        searchValue: debouncedSearchValue.length ? debouncedSearchValue : undefined
      }),
    {
      enabled: !!selectedShopId && !!startDate && !!endDate,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true
    }
  );

  const result: SubscriptionWithStatus[] = useMemo(() => {
    return (data?.items ?? []).map((item) => {
      const { reportSubmittedAt, reportAcceptedAt } = item;
      return {
        ...item,
        status: reportSubmittedAt
          ? reportAcceptedAt
            ? SubscriptionStatus.ACCEPTED
            : SubscriptionStatus.REPORTED
          : SubscriptionStatus.DEFAULT
      };
    });
  }, [data]);

  const totalRows = useMemo(() => {
    return data?.meta.totalItems ?? 0;
  }, [data]);

  return {
    data: result,
    totalRows,
    loading: isLoading,
    fetching: isFetching,
    searchValue,
    sort,
    pagination,
    requestQueryKey: queryKey,
    handleSearchChange,
    handleSortingChange,
    handlePaginationChange
  };
};
